import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {AnalyticsService} from '../../logging/analytics.service';
import {AppDealActionStyle, AppPromoTextPosition} from "../../configuration/app-entity.model";
import {NavigationService} from "../../navigation/navigation.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {PromoModel} from "../promo.model";
import {MenuItemData} from '../../menu/menu-entity.model';
import {MenuModel} from '../../menu/menu.model';
import {QrCodePageComponent} from "../../ui-elements/qr-code/qr-code-page/qr-code-page.component";
import {QrCodePanelComponent} from "../../ui-elements/qr-code/qr-code-panel/qr-code-panel.component";
import {AppPromoAction, AppPromoData, AppPromoPromotedSellableData} from '../../identity/member-summary-entity.model';
import {DealData} from '../../deal/deal-entity.model';
import {DealModel} from '../../deal/deal.model';
import {NgClass} from '@angular/common';
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {UrlPipe} from '../../utils/url.pipe';
import {LiteralPipe} from '../../utils/literal.pipe';
import {isFedeltaImageData} from '../../utils/string-utils';
import {ConfigurationModel} from '../../configuration/configuration.model';

@Component({
    selector: "woa-promo-card",
    templateUrl: "./promo-card.component.html",
    styleUrl: "./promo-card.component.scss",
    host: {
        "[class.inlineAction]": "inlineAction()",
        "[class.cornerAction]": "cornerAction()",
        "[class.peelAction]": "peelAction()",
        "[class.bottomDescription]": "bottomDescription()",
        "[class.fadeOut]": "fadeOut()",
        "[class.withAction]": "withAction()",
        "[class.typeSpecific]": "currentRestrictedOrderTypes()"
    },
    imports: [
        ButtonComponent,
        CurrencyPipe,
        IconButtonComponent,
        ImageCacheDirective,
        LiteralPipe,
        NgClass,
        QrCodePanelComponent,
        ResponsiveDirective,
        TranslateModule,
        UrlPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoCardComponent {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private navigationService: NavigationService = inject(NavigationService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private translate: TranslateService = inject(TranslateService);
    private menuModel: MenuModel = inject(MenuModel);
    private promoModel: PromoModel = inject(PromoModel);
    private dealModel: DealModel = inject(DealModel);

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    IconButtonType = IconButtonType;
    AppPromoTextPosition = AppPromoTextPosition;
    FedeltaImageData = FedeltaImageData;
    isFedeltaImageData = isFedeltaImageData;

    static readonly PROMOTED_PRODUCT_CYCLE_SECONDS: number = 10;

    public readonly title: InputSignal<string> = input<string>();
    public readonly message: InputSignal<string> = input<string>();
    public readonly image: InputSignal<string | FedeltaImageData> = input<string | FedeltaImageData>();
    public readonly autoCropImage: InputSignal<boolean> = input<boolean>(false);
    public readonly actionLabel: InputSignal<string> = input<string>();
    public readonly actionStyle: InputSignal<AppDealActionStyle> = input<AppDealActionStyle>();
    public readonly empty: InputSignal<boolean> = input<boolean>(false);
    public readonly suppressProducts: InputSignal<boolean> = input<boolean>(true);
    public readonly showCode: InputSignal<boolean> = input<boolean>(false);
    public readonly showCodeLink: InputSignal<boolean> = input<boolean>(false);
    public readonly promo: InputSignal<AppPromoData | undefined> = input<AppPromoData | undefined>();
    public readonly deal: InputSignal<DealData | undefined> = input<DealData | undefined>();

    public onActivate: OutputEmitterRef<MenuItemData> = output<MenuItemData>();

    public descriptionPosition: WritableSignal<AppPromoTextPosition> = signal<AppPromoTextPosition>(AppPromoTextPosition.BOTTOM);
    public currentProductName: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public currentProductPrice: WritableSignal<number | undefined> = signal<number | undefined>(undefined);
    public currentTitle: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public currentMessage: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public currentImage: WritableSignal<string | FedeltaImageData | undefined> = signal<string | FedeltaImageData | undefined>(undefined);
    public autoCropCurrentImage: WritableSignal<boolean> = signal<boolean>(false);
    public currentActionLabel: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public currentActionStyle: WritableSignal<AppDealActionStyle> = signal<AppDealActionStyle>(AppDealActionStyle.INLINE);
    public fadeOutTitle: WritableSignal<boolean> = signal<boolean>(false);
    public fadeOutProductDetails: WritableSignal<boolean> = signal<boolean>(false);
    public fadeOutMessage: WritableSignal<boolean> = signal<boolean>(false);
    public fadeOutImage: WritableSignal<boolean> = signal<boolean>(false);
    public menuItem: WritableSignal<MenuItemData | undefined> = signal<MenuItemData | undefined>(undefined);
    public currentRestrictedOrderTypes: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public actionHover: WritableSignal<boolean> = signal<boolean>(false);
    private appSettingsReceived: WritableSignal<boolean> = signal<boolean>(false);
    private promotedMenuItems: WritableSignal<MenuItemData[]> = signal<MenuItemData[]>([]);

    public inlineAction: Signal<boolean> = computed(() => this.currentActionStyle() === AppDealActionStyle.INLINE);
    public cornerAction: Signal<boolean> = computed(() => this.currentActionStyle() === AppDealActionStyle.CORNER);
    public peelAction: Signal<boolean> = computed(() =>
        this.currentActionStyle() === AppDealActionStyle.PEEL && !!this.currentActionLabel());
    public bottomDescription: Signal<boolean> = computed(() => this.descriptionPosition() === AppPromoTextPosition.BOTTOM);
    public fadeOut: Signal<boolean> = computed(() => this.fadeOutImage());
    public withAction: Signal<boolean> = computed(() => !!this.currentActionLabel());
    public imageData: Signal<FedeltaImageData> = computed(() => <FedeltaImageData>this.currentImage());
    public dealCode: Signal<string | undefined> = computed(() => this.deal() ? this.deal()!.dealCode : undefined);
    public hasImage: Signal<boolean> = computed(() =>
        !!this.currentImage() && (typeof this.currentImage() === "string" || !!this.imageData().id || !!this.imageData().image));
    public hasDescription: Signal<boolean> = computed(() =>
        !!this.currentTitle() || !!this.currentProductName() || !!this.currentMessage());
    public shadeImage: Signal<boolean> = computed(() =>
        this.hasImage() && this.autoCropCurrentImage() && this.hasDescription());
    private readonly cyclePromotedMenuItems: Signal<boolean> = computed(() =>
        this.promo() && this.promotedMenuItems().length > 1 && (!this.promo()!.image.id || this.promo()!.showDetailsOnPromoCard));

    private cardHover: boolean = false;
    private promotedMenuItemsCycleTimer: ReturnType<typeof setInterval>;

    constructor() {
        effect(() => {

            // Avoid double application on creation of the component
            if (this.configurationModel.app()) {
                this.appSettingsReceived.set(true);
            }
            if (!this.appSettingsReceived()) {
                return;
            }

            this.empty();

            this.descriptionPosition.set(this.configurationModel.app()?.promoTextPosition);
            this.applyCardDetails();

            // Find menu items for promoted products
            if (this.promo() && this.promo()!.promotedSellables.length && this.menuModel.currentMenu()) {
                this.promotedMenuItems.set(this.promo()!.promotedSellables
                    .map((promoted: AppPromoPromotedSellableData) => this.promoModel.getPromotableMenuItem()(promoted, this.suppressProducts() && this.promo()!.suppressPromoCardProducts))
                    .filter((menuItem: MenuItemData) => menuItem));
                if (this.promotedMenuItemsCycleTimer) {
                    clearTimeout(this.promotedMenuItemsCycleTimer);
                }
                if (this.promotedMenuItems().length) {
                    this.displayPromotedMenuItem(0);
                }
            }
        });
    }

    private displayPromotedMenuItem(idx: number): void {
        if (idx >= this.promotedMenuItems().length) {
            idx = 0;
        }
        const menuItem: MenuItemData = this.promotedMenuItems()[idx];
        this.applyCardDetails(menuItem);
        if (this.cyclePromotedMenuItems()) {
            this.promotedMenuItemsCycleTimer = setTimeout(() => {
                this.displayPromotedMenuItem(this.cardHover || this.actionHover() ? idx : idx + 1);
            }, PromoCardComponent.PROMOTED_PRODUCT_CYCLE_SECONDS * 1000);
        }
    }

    private applyCardDetails(menuItem?: MenuItemData): void {
        let title: string;
        let productName: string;
        let productPrice: number;
        let message: string;
        let restrictedOrderTypes: string;
        let actionLabel: string;
        let actionStyle: AppDealActionStyle;

        if (this.promo()) {
            title = !this.promo()!.showTitleOnPromoCard ? undefined
                : this.promo()!.appText1 ? this.promo()!.appText1
                    : this.promo()!.promotedSellables.length && this.menuModel.currentMenu() ? this.translate.instant("woa.heading.promo.default")
                        : this.promo()!.name;
            message = this.promo()!.showMessageOnPromoCard ? this.promo()!.appText2 : undefined;
            productName = menuItem && this.promo()!.showDetailsOnPromoCard ? this.menuModel.getMenuItemDisplayName(menuItem) : undefined;
            productPrice = menuItem && this.promo()!.showDetailsOnPromoCard ? menuItem.sellable.defaultPrice : undefined;
            actionLabel = !this.promo()!.showActionButtonOnPromoCard ? undefined
                : menuItem && this.promo()!.promoCardAction ? this.translate.instant("woa.button.promo." + this.promo()!.promoCardAction)
                    : this.actionLabel();

            void this.analyticsService.trackPromoShown(this.promo()!.name, "card",
                this.menuModel.getMenuItemDisplayName(menuItem), menuItem?.sellable.defaultPrice, menuItem?.sellable.plu);
        } else if (this.deal()) {
            title = this.deal()!.showTitleOnDealCard ? this.dealModel.getDealName(this.deal()!, false) : undefined;
            message = this.deal()!.description;
            actionLabel = this.deal()!.showActionButtonOnDealCard ? this.translate.instant("woa.button.deals.orderDeal") : undefined;
            restrictedOrderTypes = this.deal()!.orderAhead && this.deal()!.orderDelivery && this.deal()!.orderAtTable ? undefined
                : this.deal()!.orderAhead && this.deal()!.orderDelivery ? "ahead-delivery"
                    : this.deal()!.orderAhead && this.deal()!.orderAtTable ? "ahead-atTable"
                        : this.deal()!.orderDelivery && this.deal()!.orderAtTable ? "delivery-atTable"
                            : this.deal()!.orderAhead ? "ahead"
                                : this.deal()!.orderDelivery ? "delivery"
                                    : this.deal()!.orderAtTable ? "atTable"
                                        : undefined;
        } else {
            title = this.title();
            message = this.message();
            actionLabel = this.actionLabel();
            menuItem = undefined;
        }
        actionStyle = this.actionStyle() || this.configurationModel.app()?.dealActionStyle;

        const {image, autoCropImage}: {
            image: string | FedeltaImageData,
            autoCropImage: boolean
        } = this.getImage(menuItem);

        this.fadeOutTitle.set(this.currentTitle() !== title);
        this.fadeOutProductDetails.set(this.currentProductName() !== productName);
        this.fadeOutMessage.set(this.currentMessage() !== message);
        this.fadeOutImage.set(this.currentImage() !== image && this.cyclePromotedMenuItems());

        setTimeout(() => {
            this.menuItem.set(menuItem);
            this.fadeOutTitle.set(false);
            this.fadeOutProductDetails.set(false);
            this.fadeOutMessage.set(false);
            this.fadeOutImage.set(false);
            this.currentTitle.set(title);
            this.currentProductName.set(productName);
            this.currentProductPrice.set(productPrice);
            this.currentMessage.set(message);
            this.currentRestrictedOrderTypes.set(restrictedOrderTypes);
            this.currentImage.set(image);
            this.autoCropCurrentImage.set(autoCropImage);
            this.currentActionLabel.set(actionLabel);
            this.currentActionStyle.set(actionStyle);
        }, 1000);
    }

    private getImage(menuItem: MenuItemData): { image: string | FedeltaImageData, autoCropImage: boolean } {
        if (this.promo() && this.promo()!.image.id) {
            return {image: this.promo()!.image, autoCropImage: this.promo()!.imageAutoCrop};
        }
        if (menuItem) {
            const image: FedeltaImageData = this.menuModel.itemImage()(undefined, menuItem, !!this.promo());
            if (image) {
                return {image: image, autoCropImage: this.promo() && this.promo()!.imageAutoCrop};
            }
        }
        if (this.deal() && this.deal()!.image.id) {
            return {image: this.deal()!.image, autoCropImage: this.deal()!.imageAutoCrop};
        }
        if (this.deal() && this.deal()!.template.image.id) {
            return {image: this.deal()!.template.image, autoCropImage: this.deal()!.template.imageAutoCrop};
        }
        if (this.image()) {
            return {image: this.image(), autoCropImage: this.autoCropImage()};
        }
        if (this.configurationModel.app()?.defaultImage) {
            return {image: "data:image/png;base64," + this.configurationModel.app()?.defaultImage, autoCropImage: true};
        }
        return {image: undefined, autoCropImage: false};
    }

    cardMouseOver(): void {
        this.cardHover = true;
    }

    cardMouseOut(): void {
        this.cardHover = false;
    }

    actionMouseOver(): void {
        this.actionHover.set(true);
    }

    actionMouseOut(): void {
        this.actionHover.set(false);
    }

    async showDealQRCode(): Promise<void> {
        if (this.deal()) {
            return QrCodePageComponent.showQrCode(this.navigationService, this.deal()!.dealCode, this.translate.instant("woa.label.deals.qrCode"));
        }
    }

    performAction(onlyWhenNoButton: boolean = false): void {
        if (this.promo() && !this.promo()!.promoCardAction) {
            return;
        }
        if (onlyWhenNoButton && this.currentActionLabel()) {
            return;
        }
        if (this.promo()) {
            switch (this.promo()!.promoCardAction) {
                case AppPromoAction.ORDER_PRODUCT:
                    if (!this.menuItem()) {
                        return;
                    }

                    void this.analyticsService.trackPromoItemOrdered(this.promo()!.name, "card",
                        this.menuModel.getMenuItemDisplayName(this.menuItem()), this.menuItem()?.sellable.defaultPrice, this.menuItem()?.sellable.plu);
                    break;
                case AppPromoAction.SHOW_PRODUCTS:
                    void this.analyticsService.trackPromoItemsShown(this.promo()!.name, "card");
                    break;
            }
        }
        this.onActivate.emit(this.menuItem());
    }
}
